import type { PropsWithChildren } from 'react';
import React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import type { Icon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { TypedTranslate } from '@change-corgi/core/react/i18n';

import { useBannerActions } from './hooks/useBannerActions';

export const BannerActions: React.FC<BannerActionsProps> = ({ icon, title, description, placement }) => {
	const {
		data: { draftModeTranslationValue },
		actions,
	} = useBannerActions();

	return (
		<div role="note" className="bg-gradient-background-dark-2 text-typography-inverse-strong py-4">
			<div className="container flex gap-3">
				{React.cloneElement(icon, {
					...icon.props,
					size: 24,
					className: cn(
						icon.props.className,
						'text-typography-inverse-strong flex min-h-[24px] min-w-[24px] items-center justify-center',
					),
				})}
				<div className="flex flex-col gap-4">
					<div className="flex flex-col md:block md:space-x-2">
						<Text variant="body" size="large" fontWeight="bold">
							{title}
						</Text>
						<Text variant="body">{description}</Text>
					</div>
					<div className="flex gap-2">
						<Button
							variant="tertiary"
							size="x-small"
							className="text-typography-inverse-strong"
							data-qa="banner-actions-toggle-draft-mode"
							onClick={() => actions.toggleDraftMode(placement)}
							type="button"
						>
							<TypedTranslate value={draftModeTranslationValue} />
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

type BannerActionsProps = PropsWithChildren<{
	title: React.ReactElement;
	description: React.ReactElement;
	icon: React.ReactElement<Icon>;
	placement: 'top' | 'bottom';
}>;
